import { z } from 'zod';
import type { ProbeTraceStep } from '@terra/shared-contracts-assets';

const ProbeRequestSchema = z
  .object({
    method: z.string().optional(),
    url: z.string().optional(),
    path: z.string().optional(),
    headers: z.record(z.string(), z.unknown()).optional(),
    body: z.unknown().optional(),
  })
  .passthrough();

export type ProbeHttpExchange = {
  method: string;
  url: string;
  path: string;
  statusCode?: number;
  requestHttp: string;
  responseHttp: string;
  responseSizeBytes: number;
};

const utf8ByteLength = (value: string): number =>
  new TextEncoder().encode(value).length;

const toPrettyBody = (value: unknown): string => {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const resolvePath = ({
  url,
  path,
}: {
  url?: string;
  path?: string;
}): string => {
  if (path?.trim()) {
    return path.trim();
  }

  if (!url?.trim()) {
    return '/';
  }

  try {
    const parsed = new URL(url);

    return `${parsed.pathname}${parsed.search}` || '/';
  } catch {
    return url.startsWith('/') ? url : `/${url}`;
  }
};

const resolveHost = (url?: string): string | undefined => {
  if (!url?.trim()) {
    return undefined;
  }

  try {
    return new URL(url).host;
  } catch {
    return undefined;
  }
};

const formatHeaders = (
  headers: Record<string, unknown> | undefined,
): string[] => {
  if (!headers) {
    return [];
  }

  return Object.entries(headers).flatMap(([key, value]) => {
    if (value === undefined || value === null) {
      return [];
    }

    if (Array.isArray(value)) {
      return value.map((entry) => `${key}: ${String(entry)}`);
    }

    return [`${key}: ${String(value)}`];
  });
};

export const parseProbeHttpExchange = ({
  step,
}: {
  step: ProbeTraceStep;
}): ProbeHttpExchange | null => {
  const statusFromLabel = step.statusLabel
    ? Number.parseInt(step.statusLabel, 10)
    : Number.NaN;
  const statusCode = Number.isFinite(statusFromLabel)
    ? statusFromLabel
    : undefined;

  let method = 'GET';
  let url = '';
  let path = '/';
  let requestHeaders: Record<string, unknown> | undefined;
  let requestBody = '';

  if (step.code?.trim()) {
    try {
      const parsed = ProbeRequestSchema.safeParse(JSON.parse(step.code));

      if (parsed.success) {
        method = (parsed.data.method ?? method).toUpperCase();
        url = parsed.data.url?.trim() ?? '';
        path = resolvePath({ url, path: parsed.data.path });
        requestHeaders = parsed.data.headers;
        requestBody = toPrettyBody(parsed.data.body);
      } else {
        requestBody = step.code.trim();
      }
    } catch {
      requestBody = step.code.trim();
    }
  }

  const responseBody = step.result?.trim() ?? '';
  const parsedRequest =
    Boolean(url) ||
    Boolean(requestHeaders) ||
    Boolean(requestBody && step.code?.trim()?.startsWith('{'));
  const hasNumericStatus = statusCode !== undefined;

  if (!parsedRequest && !hasNumericStatus) {
    return null;
  }

  if (!step.code?.trim() && !responseBody) {
    return null;
  }

  const host = resolveHost(url);
  const headerLines = formatHeaders(requestHeaders);
  const requestLines = [
    `${method} ${path} HTTP/1.1`,
    ...(host ? [`Host: ${host}`] : []),
    ...headerLines,
    ...(requestBody ? ['', requestBody] : ['']),
  ];

  const statusLine =
    statusCode === undefined ? 'HTTP/1.1 000' : `HTTP/1.1 ${statusCode}`;
  const responseLines = [
    statusLine,
    ...(responseBody
      ? ['Content-Type: application/json', '', responseBody]
      : ['']),
  ];

  return {
    method,
    url: url || path,
    path,
    statusCode,
    requestHttp: requestLines.join('\n'),
    responseHttp: responseLines.join('\n'),
    responseSizeBytes: utf8ByteLength(responseBody),
  };
};

export const stepHasProbeHttpExchange = ({
  step,
}: {
  step: ProbeTraceStep;
}): boolean => parseProbeHttpExchange({ step }) !== null;
